import React, { useEffect, useState } from 'react'
import { Card, Button, Avatar, Space, Tag, Typography, Spin, Row, Col, theme, Pagination, Divider } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  EyeOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  FilterOutlined
} from '@ant-design/icons'
import PlaceholderImage from '../../../../assets/images/MH-logo-v1 1.png'

const { Text, Title } = Typography

const SearchResultsMobile = ({
  results = [],
  loading = false,
  onViewAvailability,
  searchFilters = {},
  searchType,
  currentPage = 1,
  totalPages = 1,
  totalResults = 0,
  onPageChange,
  onBackToRefinement
}) => {
  const [showAnimation, setShowAnimation] = useState(false) // Start with no animation
  const [animationComplete, setAnimationComplete] = useState(false)
  const { token } = theme.useToken()

  // Control animation timing with failsafe
  useEffect(() => {
    console.log('🔍 SearchResultsMobile mounted or loading changed:', { loading, results: results.length })

    if (loading) {
      console.log('🔄 Search started - showing animation for 1.5 seconds')
      setShowAnimation(true)
      setAnimationComplete(false)

      // Show animation for 1.5 seconds
      const timer = setTimeout(() => {
        console.log('✅ 1.5 seconds complete - hiding animation')
        setShowAnimation(false)
        setAnimationComplete(true)
      }, 1500)

      return () => clearTimeout(timer)
    }
  }, [loading])

  // Failsafe: If we have results and no loading, show them immediately
  useEffect(() => {
    if (!loading && results.length > 0 && !animationComplete) {
      console.log('🚨 Failsafe: Showing results immediately')
      setShowAnimation(false)
      setAnimationComplete(true)
    }
  }, [loading, results.length, animationComplete])

  // Initial render handling
  useEffect(() => {
    if (!loading && !showAnimation && !animationComplete) {
      console.log('📋 Initial render - showing content')
      setAnimationComplete(true)
    }
  }, [loading, showAnimation, animationComplete])

  const handleViewProfile = (doctor) => {
    onViewAvailability?.(doctor)
  }

  const displayResults = results.length > 0 ? results : []

  // Helper functions
  const getDoctorName = (doctor) => {
    return `${doctor.firstName} ${doctor.lastName}`.trim()
  }

  const isDoctorActive = (doctor) => {
    return doctor.extraData?.isActive !== false
  }

  const formatNextAvailableSlot = (nextAvailableSlot) => {
    if (!nextAvailableSlot || !nextAvailableSlot.date || !nextAvailableSlot.timeSlot) {
      return 'No availability'
    }

    const { date, timeSlot } = nextAvailableSlot
    const slotDate = new Date(date)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    let dateText = ''
    if (slotDate.toDateString() === today.toDateString()) {
      dateText = 'today'
    } else if (slotDate.toDateString() === tomorrow.toDateString()) {
      dateText = 'tomorrow'
    } else {
      const dayDiff = Math.ceil((slotDate - today) / (1000 * 60 * 60 * 24))
      if (dayDiff <= 7) {
        dateText = slotDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
      } else {
        dateText = slotDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    return `Available ${dateText} at ${timeSlot.time}`
  }

  // Helper function to format quick booking slots
  const formatQuickSlot = (slot) => {
    if (!slot || !slot.date || !slot.time) {
      return 'Invalid slot'
    }

    const slotDate = new Date(slot.date)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    let dateText = ''
    if (slotDate.toDateString() === today.toDateString()) {
      dateText = 'Today'
    } else if (slotDate.toDateString() === tomorrow.toDateString()) {
      dateText = 'Tomorrow'
    } else {
      // Format as day of week if within the next 7 days
      const dayDiff = Math.ceil((slotDate - today) / (1000 * 60 * 60 * 24))
      if (dayDiff <= 7) {
        dateText = slotDate.toLocaleDateString('en-US', { weekday: 'short' })
      } else {
        dateText = slotDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    return `${dateText} ${slot.time}`
  }

  // Handler for quick booking
  const handleQuickBook = (doctor, slot) => {
    // Navigate directly to availability view with pre-selected slot
    onViewAvailability?.(doctor, slot)
  }

  // Get consultation cost for display purposes (all virtual consultations show pricing)
  const getConsultationCost = (doctor) => {
    if (searchFilters.searchType !== 'virtual_consultation') return 0

    // Demo mode amounts for testing CallPay integration
    if (searchFilters.serviceType === 'flexi') return 10 // Was 250, now R10 for demo
    if (searchFilters.serviceType === 'priority') return 101 // Was 450, now R101 for demo
    // For scheduled consultations, use clinic's rate
    return doctor.clinicRecord?.virtualConsultationCost || 0
  }

  const MobileDoctorCard = ({ doctor }) => (
    <Card
      style={{
        marginBottom: '12px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        opacity: isDoctorActive(doctor) ? 1 : 0.7,
        ...(doctor._id === 'virtual-doctor-24-7' && {
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
        })
      }}
      bodyStyle={{
        padding: '16px',
        position: 'relative', // Add relative positioning for absolute pricing
        ...(doctor._id === 'virtual-doctor-24-7' && {
          color: 'white'
        })
      }}
    >
      {/* Header with Avatar and Basic Info */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
        <Avatar
          size={60}
          src={doctor.profileImage || doctor.image || PlaceholderImage}
          icon={<UserOutlined />}
          style={{
            border:
              doctor._id === 'virtual-doctor-24-7'
                ? '2px solid rgba(255, 255, 255, 0.8)'
                : `2px solid ${token.colorPrimary}`,
            marginRight: '12px',
            flexShrink: 0
          }}
        />

        {/* Virtual Consultation Pricing - Top Right Corner */}
        {searchFilters.searchType === 'virtual_consultation' && getConsultationCost(doctor) > 0 && (
          <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
            <div
              style={{
                background:
                  doctor._id === 'virtual-doctor-24-7'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'linear-gradient(135deg, #34d399 0%, #10b981 100%)', // Lighter green
                padding: '6px 10px',
                borderRadius: '8px',
                border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                boxShadow: doctor._id === 'virtual-doctor-24-7' ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.4)',
                minWidth: '70px',
                textAlign: 'center'
              }}
            >
              <Text
                style={{
                  fontSize: '10px',
                  color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '500',
                  display: 'block',
                  textAlign: 'center',
                  marginBottom: '2px'
                }}
              >
                Virtual
              </Text>
              <Text
                style={{
                  fontSize: '14px',
                  color: 'white',
                  fontWeight: '700',
                  display: 'block',
                  textAlign: 'center'
                }}
              >
                R{getConsultationCost(doctor)}
              </Text>
            </div>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <Title
              level={5}
              style={{
                margin: 0,
                color: doctor._id === 'virtual-doctor-24-7' ? 'white' : '#111827',
                fontSize: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              {getDoctorName(doctor)}
            </Title>
            {doctor._id === 'virtual-doctor-24-7' && (
              <Tag
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontWeight: '600',
                  fontSize: '10px',
                  marginLeft: '8px'
                }}
              >
                Featured
              </Tag>
            )}
          </div>

          <Text
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : '#666',
              fontSize: '14px',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {doctor.extraData.profession?.charAt(0).toUpperCase() + doctor.extraData.profession?.slice(1).toLowerCase()}
          </Text>
        </div>
      </div>

      {/* Location and Availability Info */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <EnvironmentOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'red',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}
          >
            {doctor.clinicRecord?._id === 'virtual-doctor-24-7' ? (
              'Virtual Consultation Available'
            ) : doctor.clinicRecord?.name ? (
              <>
                {doctor.clinicRecord.name}
                {doctor.clinicRecord?.distanceFormatted && (
                  <span
                    style={{
                      marginLeft: '8px',
                      color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.6)' : '#52c41a',
                      fontWeight: '500'
                    }}
                  >
                    • {doctor.clinicRecord.distanceFormatted}
                  </span>
                )}
              </>
            ) : (
              'Location not specified'
            )}
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <CalendarOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'blue',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666'
            }}
          >
            {doctor._id === 'virtual-doctor-24-7'
              ? 'Available 24/7 - Instant Access'
              : doctor.nextAvailableSlots && doctor.nextAvailableSlots.length > 0
              ? `${doctor.nextAvailableSlots.length} slots available`
              : formatNextAvailableSlot(doctor.nextAvailableSlot)}
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ThunderboltOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'goldenrod',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666'
            }}
          >
            Instant Booking Available
          </Text>
        </div>

        {/* Quick Booking Slots */}
        {doctor._id !== 'virtual-doctor-24-7' && doctor.nextAvailableSlots && doctor.nextAvailableSlots.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#f8fafc',
              borderRadius: '6px',
              border: '1px solid #e2e8f0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
              <ThunderboltOutlined
                style={{
                  fontSize: '12px',
                  color: '#f59e0b',
                  marginRight: '4px'
                }}
              />
              <Text
                style={{
                  fontSize: '11px',
                  color: '#374151',
                  fontWeight: '600'
                }}
              >
                Quick Book
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}
            >
              {doctor.nextAvailableSlots.slice(0, 3).map((slot, index) => (
                <Button
                  key={index}
                  size='small'
                  type='primary'
                  icon={<ThunderboltOutlined style={{ fontSize: '8px' }} />}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleQuickBook(doctor, slot)
                  }}
                  style={{
                    fontSize: '10px',
                    height: '26px',
                    padding: '0 10px',
                    borderRadius: '4px',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    borderColor: 'transparent',
                    boxShadow: '0 1px 3px rgba(59, 130, 246, 0.2)'
                  }}
                >
                  {formatQuickSlot(slot)}
                </Button>
              ))}
              <Button
                type='text'
                size='small'
                icon={<EyeOutlined />}
                onClick={() => handleViewProfile(doctor)}
                disabled={!isDoctorActive(doctor)}
                style={{
                  fontSize: '10px',
                  color: token.colorPrimary,
                  fontWeight: '500',
                  padding: '0 6px',
                  marginLeft: '4px'
                }}
              >
                View All
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}></div>
    </Card>
  )

  console.log('🔍 SearchResultsMobile render check:', {
    loading,
    showAnimation,
    animationComplete,
    resultsLength: results.length
  })

  if (showAnimation || (!animationComplete && loading)) {
    console.log('✨ Showing loading animation!')
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          margin: '16px'
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px'
          }}
        >
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '3px solid rgba(59, 130, 246, 0.2)',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              position: 'absolute'
            }}
          />
          <div
            style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite'
            }}
          >
            <SearchOutlined style={{ fontSize: '18px', color: 'white' }} />
          </div>
        </div>

        <Title
          level={4}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 8px 0',
            fontSize: '18px',
            textAlign: 'center'
          }}
        >
          Finding Your Perfect Doctor
        </Title>
        <Text style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
          Searching through our network...
        </Text>
      </div>
    )
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Navigation */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type='text'
              icon={<ArrowLeftOutlined />}
              onClick={onBackToRefinement}
              style={{ marginRight: '12px' }}
            />
            <div>
              <Title level={5} style={{ margin: 0, fontSize: '16px', color: '#111827' }}>
                {totalResults > 0 ? `${totalResults} Doctors Found` : 'Search Results'}
              </Title>
              {totalResults > 0 && (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  Page {currentPage} of {totalPages}
                </Text>
              )}
            </div>
          </div>
          <Button
            type='text'
            icon={<FilterOutlined />}
            onClick={onBackToRefinement}
            style={{ color: token.colorPrimary }}
          >
            Filters
          </Button>
        </div>
      </div>

      {/* Scrollable Results Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }} className='mobile-search-content'>
        {/* Results List */}
        {displayResults.length > 0 ? (
          <>
            {displayResults.map((doctor) => (
              <MobileDoctorCard key={doctor._id} doctor={doctor} />
            ))}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '80px' }}>
                <Pagination
                  current={currentPage}
                  total={totalResults}
                  pageSize={10}
                  onChange={onPageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} doctors`}
                  style={{ fontSize: '14px' }}
                />
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: '#f9fafb',
              borderRadius: '12px',
              marginTop: '20px'
            }}
          >
            <SearchOutlined style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#9ca3af', margin: '0 0 8px 0' }}>
              No Doctors Found
            </Title>
            <Text style={{ color: '#6b7280' }}>Try adjusting your search filters or location</Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResultsMobile
